"use client";
import React, { useEffect, useState } from "react";
import {
  userPoint,
  userPointsLedger,
  generatePointsTopupLink,
} from "../../../../services/billing";
import Breadcrumb from "../../../../utility/breadcrumb/Breadcrumb";
import { useRazorpay } from "../../../../context/RazorpayContext";
import RazorpayModal from "../Razorpay/RazorpayModal";
import PointsLedger from "./PointsLedger";

const PointsDashboard = () => {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState("");
  const [consumeAmount, setConsumeAmount] = useState("");
  const { handleTopupPoints, razorpayModalData } = useRazorpay();
  const afterPaymentSuccessNavigationUrl = "/billing-section";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const pointsRes = await userPoint();
      setPoints(pointsRes?.data?.data);
    } catch (err) {
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    return {
      burnRate: 0, // keep if needed later
      daysLeft: 0, // keep if needed later
      totalSpent: points?.spend_balance || 0,
      usagePercentage: points?.remaining_percentage ?? 100,
    };
  };

  const metrics = calculateMetrics();

  const getPointsStatus = (val = 0) => {
    if (val > 500) return "good";
    if (val > 200) return "medium";
    return "low";
  };

  const getPointsMessage = (val = 0) => {
    if (val > 500) return "You have a healthy points balance";
    if (val > 200) return "You have a moderate points balance";
    return "Low points balance. Please top up to continue.";
  };

  const formatPoints = (value = 0) => {
    return new Intl.NumberFormat("en-IN").format(value);
  };

  return (
    <>
      {razorpayModalData?.isRazorPayModal && (
        <RazorpayModal navigationUrl={afterPaymentSuccessNavigationUrl} />
      )}

      <div className="without-datepicker-breadcrumb">
        <Breadcrumb />
      </div>
      <div className="points-page">
        {/* HERO BANNER */}
        <div className="points-banner">
          <div className="points-overview-content">
            <h1 className="points-balance">
              {formatPoints(points?.balance || 0)}{" "}
              <span style={{ fontSize: "32px", opacity: 0.8 }}>pts</span>
            </h1>
            <p className="points-label">Total Outstanding Balance</p>
            <div
              className={`points-alert-modern ${getPointsStatus(points?.balance)}`}
            >
              {getPointsMessage(points?.balance)}
            </div>
          </div>
          <div className="points-icon-wrapper-huge">
            <i className="bi bi-star-fill"></i>
          </div>
        </div>

        {/* OVERLAPPING CARDS GRID */}
        <div className="points-overlap-container">
          <div className="points-overlap-grid">
            {/* CARD 1: BUY POINTS (RESTORED TO ORIGINAL) */}
            <div className="points-neo-card">
              <div className="points-neo-title">
                <span>Buy Points</span>
                <i
                  className="bi bi-star"
                  style={{
                    color: "var(--actionable-active-color)",
                    fontSize: "20px",
                  }}
                ></i>
              </div>

              <div className="points-neo-input-box">
                <input
                  type="number"
                  min="1"
                  placeholder="Enter Amount"
                  value={topupAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Only allow digits, prevent leading zeros unless it's just an empty string
                    if (val === "" || /^[1-9][0-9]*$/.test(val)) {
                      setTopupAmount(val);
                    }
                  }}
                  style={{ paddingRight: "25px" }}
                />
                <span className="currency">₹</span>
              </div>

              <div className="points-neo-chips">
                {[500, 1000, 5000].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setTopupAmount(String(amt))}
                    className={Number(topupAmount) === amt ? "active" : ""}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>

              <button
                className="points-neo-btn mb-2"
                onClick={(e) => handleTopupPoints(e, topupAmount)}
                disabled={!topupAmount || Number(topupAmount) <= 0}
              >
                Buy Points +
              </button>

              <div className="usage-info-alert">
                <i className="bi bi-info-circle"></i>
                Secure payments powered by Razorpay
              </div>
            </div>

            {/* CARD 2: NEW USAGE FORECAST DESIGN */}
            <div className="points-neo-card usage-forecast-card">
              <div className="usage-forecast-header">
                <h2>Usage Forecast</h2>
                <div className="header-icon">
                  <i className="bi bi-graph-up"></i>
                </div>
              </div>

              <div className="current-usage-section">
                {/* <div className="section-label">Current Usage</div> */}
                <div className="usage-box-neo">
                  <div className="usage-box-header">
                    <div className="usage-main-info">
                      <div className="usage-icon-circle">
                        <i className="bi bi-graph-up-arrow"></i>
                      </div>
                      <div className="usage-text-data">
                        <div className="usage-value">
                          {formatPoints(points?.balance || 0)} <span>pts</span>
                        </div>
                        <div className="usage-label">Total points remain</div>
                      </div>
                    </div>
                    <div className="usage-badge">
                      {metrics.usagePercentage}% remaining
                    </div>
                  </div>
                  <div className="usage-progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${metrics.usagePercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="usage-summary-section">
                <div className="summary-label">Usage Summary</div>
                <div className="summary-icon-centered">
                  <i className="bi bi-pie-chart"></i>
                </div>
                <div className="summary-value-large">
                  {metrics.totalSpent} <span>pts</span>
                </div>
                <div className="summary-subtext">Total points used so far</div>
              </div>
            </div>
          </div>

          {/* TRANSACTIONS LEDGER */}
          <div>
            <PointsLedger />
          </div>
        </div>
      </div>
    </>
  );
};

export default PointsDashboard;
